import { FC, ReactNode } from "react";
import { Button, Flex, Spinner } from "@chakra-ui/react";

import { CASE_DOCUMENT_STATUS, CASE_FILE_KIND, CaseDocumentModel } from "@shared/models";
import { CheckIcon } from "@shared/components/Icons";
import { FONT_WEIGHT } from "@assets/theme/foundations/textStyle";

import { getParsingProgress } from "./CaseDocuments.utils";

const TONES = {
  neutral: { bg: "grey.5", color: "blue.700" },
  active: { bg: "brand.primary/10", color: "brand.primary" },
  success: { bg: "score.highBg", color: "score.highText" },
  warning: { bg: "score.midBg", color: "score.midText" },
  error: { bg: "score.lowBg", color: "score.lowText" },
};

interface StatusPillProps {
  tone: keyof typeof TONES;
  children: ReactNode;
  onClick?: () => void;
}

const StatusPill: FC<StatusPillProps> = ({ tone, children, onClick }) => (
  <Flex
    {...TONES[tone]}
    {...(onClick && { asChild: true, cursor: "pointer", _hover: { filter: "brightness(0.95)" } })}
    align="center"
    gap="5px"
    flexShrink={0}
    h="24px"
    px="10px"
    borderRadius="full"
    fontSize="12px"
    fontWeight={FONT_WEIGHT.HEAVY}
    whiteSpace="nowrap"
  >
    {onClick ? (
      <Button unstyled onClick={onClick}>
        {children}
      </Button>
    ) : (
      children
    )}
  </Flex>
);

const DonePill: FC<{ label: string }> = ({ label }) => (
  <StatusPill tone="success">
    <CheckIcon />
    {label}
  </StatusPill>
);

interface CaseDocumentStatusBadgeProps {
  caseDocument: CaseDocumentModel;
  onChooseValues?: () => void;
}

const MedicalSummariesBadges: FC<CaseDocumentStatusBadgeProps> = ({
  caseDocument: { pending_choices, flagged_fields },
  onChooseValues,
}) => {
  if (!pending_choices && !flagged_fields) return <DonePill label="Parsed" />;

  return (
    <Flex gap="6px" wrap="wrap" justify="flex-end">
      {pending_choices > 0 && (
        <StatusPill tone="warning" onClick={onChooseValues}>
          {pending_choices} to choose
        </StatusPill>
      )}
      {flagged_fields > 0 && <StatusPill tone="warning">{flagged_fields} flagged</StatusPill>}
    </Flex>
  );
};

const ParsingPill: FC<{ caseDocument: CaseDocumentModel }> = ({ caseDocument }) => {
  // The PSR reports no progress while it's parsed.
  if (caseDocument.kind === CASE_FILE_KIND.PSR) {
    return (
      <StatusPill tone="neutral">
        <Spinner size="xs" />
        Parsing
      </StatusPill>
    );
  }

  return <StatusPill tone="active">Parsing {getParsingProgress(caseDocument)}%</StatusPill>;
};

const CaseDocumentStatusBadge: FC<CaseDocumentStatusBadgeProps> = ({ caseDocument, onChooseValues }) => {
  const { kind, status } = caseDocument;

  if (status === CASE_DOCUMENT_STATUS.PARSING) return <ParsingPill caseDocument={caseDocument} />;
  if (status === CASE_DOCUMENT_STATUS.FAILED) return <StatusPill tone="error">Parsing failed</StatusPill>;
  if (kind === CASE_FILE_KIND.MEDICAL)
    return <MedicalSummariesBadges caseDocument={caseDocument} onChooseValues={onChooseValues} />;

  return <DonePill label={kind === CASE_FILE_KIND.NOTES ? "Saved" : "Parsed"} />;
};

export default CaseDocumentStatusBadge;
